import React, { useEffect, useState } from 'react';
import { Globe, ChevronDown, ChevronUp, Loader2, Key, Save, Check } from 'lucide-react';
import { CrawlStatusResponse } from '../../types/create';
import { Provider } from 'pages/Knowledge/types';

interface WebsiteCrawlSectionProps {
  websiteUrl: string;
  setWebsiteUrl: (url: string) => void;
  selectedProvider: string;
  setSelectedProvider: (provider: string) => void;
  crawlOptions: {
    crawlSubPages: boolean;
    onlyMainContent: boolean;
    includes: string;
    excludes: string;
    limit: number;
    useSitemap: boolean;
    jinaApiKey?: string;
    firecrawlApiKey?: string;
  };
  setCrawlOptions: (options: any) => void;
  isCrawling: boolean;
  crawlResults: CrawlStatusResponse | null;
  selectedPages: Set<number>;
  isOptionsExpanded: boolean;
  setIsOptionsExpanded: (expanded: boolean) => void;
  onStartCrawl: (provider: string) => void;
  onPageSelection: (pageIndex: number, checked: boolean) => void;
  onSelectAll: (checked: boolean) => void;
  onResetResults: () => void;
}

const WebsiteCrawlSection: React.FC<WebsiteCrawlSectionProps> = ({
  websiteUrl,
  setWebsiteUrl,
  selectedProvider,
  setSelectedProvider,
  crawlOptions,
  setCrawlOptions,
  isCrawling,
  crawlResults,
  selectedPages,
  isOptionsExpanded,
  setIsOptionsExpanded,
  onStartCrawl,
  onPageSelection,
  onSelectAll,
  onResetResults
}) => {
  const [showApiKeyInput, setShowApiKeyInput] = useState<string | null>(null);
  const [tempApiKey, setTempApiKey] = useState<string>('');

  useEffect(() => {
    console.log("Crawl results updated:", crawlResults);
  }, [crawlResults])

  const handleApiKeyClick = (provider: string) => {
    const currentKey = provider === Provider.Jina ? crawlOptions.jinaApiKey : crawlOptions.firecrawlApiKey;
    setTempApiKey(currentKey || '');
    setShowApiKeyInput(provider);
  };

  const handleSaveApiKey = () => {
    if (showApiKeyInput) {
      const updatedOptions = {
        ...crawlOptions,
        [showApiKeyInput === Provider.Jina ? 'jinaApiKey' : 'firecrawlApiKey']: tempApiKey
      };
      setCrawlOptions(updatedOptions);
      setShowApiKeyInput(null);
      setTempApiKey('');
    }
  };

  const handleCancelApiKey = () => {
    setShowApiKeyInput(null);
    setTempApiKey('');
  };

  const handleProviderSelection = (provider: string) => {
    setSelectedProvider(provider);

    // If API key input is open, switch to the new provider's API key
    if (showApiKeyInput) {
      const newKey = provider === Provider.Jina ? crawlOptions.jinaApiKey : crawlOptions.firecrawlApiKey;
      setTempApiKey(newKey || '');
      setShowApiKeyInput(provider);
    }
  };

  const isRunDisabled = () => {
    if (isCrawling || !websiteUrl.trim()) return true;

    // Check if selected provider has API key
    if (selectedProvider === Provider.Jina && !crawlOptions.jinaApiKey) return true;
    if (selectedProvider === Provider.Firecrawl && !crawlOptions.firecrawlApiKey) return true;

    return false;
  };


  return (
    <div>
      <h3 className="text-xs mb-2 text-gray-600">Select a provider</h3>

      {/* Provider Selection */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-4">
        <div
          className={`border rounded-md p-2 cursor-pointer hover:border-gray-300 ${selectedProvider === 'jina' ? 'border-blue-500 bg-blue-50' : 'border-gray-200'
            }`}
          onClick={() => handleProviderSelection(Provider.Jina)}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <Globe className={`w-4 h-4 ${selectedProvider === Provider.Jina ? 'text-blue-600' : 'text-gray-600'}`} />
              <span className="text-xs font-medium">Jina Reader</span>
              {selectedProvider === Provider.Jina && (
                <span className="bg-blue-100 text-blue-800 text-[10px] px-1.5 py-0.5 rounded">Selected</span>
              )}
            </div>
            {crawlOptions.jinaApiKey ? (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleApiKeyClick(Provider.Jina);
                }}
                className="flex items-center space-x-1 text-green-600 hover:text-green-700 p-1 hover:bg-green-50 rounded"
                title="Modify API Key"
              >
                <Check className="w-3 h-3" />
                <span className="text-[10px] font-medium">Configured</span>
              </button>
            ) : (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleApiKeyClick(Provider.Jina);
                }}
                className="p-1 hover:bg-gray-100 rounded"
                title="Configure API Key"
              >
                <div className='flex items-center space-x-1'>
                  <Key className="w-3 h-3 text-gray-500 " /><span className="text-[10px] font-medium">Set API key</span>
                </div>
              </button>
            )}
          </div>
        </div>

        <div
          className={`border rounded-md p-2 cursor-pointer hover:border-gray-300 ${selectedProvider === 'firecrawl' ? 'border-orange-500 bg-orange-50' : 'border-gray-200'
            }`}
          onClick={() => handleProviderSelection(Provider.Firecrawl)}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <Globe className="w-4 h-4 text-orange-600" />
              <span className="text-xs font-medium">Firecrawl</span>
              {selectedProvider === Provider.Firecrawl && (
                <span className="bg-orange-100 text-orange-800 text-[10px] px-1.5 py-0.5 rounded">Selected</span>
              )}
            </div>
            {crawlOptions.firecrawlApiKey ? (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleApiKeyClick(Provider.Firecrawl);
                }}
                className="flex items-center space-x-1 text-green-600 hover:text-green-700 p-1 hover:bg-green-50 rounded"
                title="Modify API Key"
              >
                <Check className="w-3 h-3" />
                <span className="text-[10px] font-medium">Configured</span>
              </button>
            ) : (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleApiKeyClick(Provider.Firecrawl);
                }}
                className="p-1 hover:bg-gray-100 rounded"
                title="Configure API Key"
              >
                <div className='flex items-center space-x-1'>
                  <Key className="w-3 h-3 text-gray-500 " /><span className="text-[10px] font-medium">Set API key</span>
                </div>
              </button>
            )}
          </div>
        </div>
      </div>

      {/* API Key Input Modal */}
      {showApiKeyInput && (
        <div className="mb-4 p-3 bg-gray-50 border border-gray-200 rounded-lg">
          <div className="flex items-center space-x-2 mb-2">
            <Key className="w-4 h-4 text-gray-600" />
            <span className="text-sm font-medium">
              {showApiKeyInput === Provider.Jina ? 'Jina Reader' : 'Firecrawl'} API Key
            </span>
          </div>
          <div className="flex space-x-2">
            <input
              type="text"
              value={tempApiKey}
              onChange={(e) => setTempApiKey(e.target.value)}
              placeholder="Enter API key..."
              className="flex-1 px-3 py-2 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            <button
              onClick={handleSaveApiKey}
              className="px-3 py-2 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 flex items-center space-x-1"
            >
              <Save className="w-3 h-3" />
              <span>Save</span>
            </button>
            <button
              onClick={handleCancelApiKey}
              className="px-3 py-2 bg-gray-200 text-gray-700 text-sm rounded hover:bg-gray-300"
            >
              Cancel
            </button>
          </div>
          <p className="text-xs text-gray-500 mt-1">
            {showApiKeyInput === Provider.Jina
              ? 'Get your API key from Jina AI dashboard'
              : 'Get your API key from Firecrawl dashboard'
            }
          </p>
        </div>
      )}

      {/* Extract web content section */}
      <div className="mb-4">
        <div className="flex items-center space-x-2 mb-3">
          <Globe className="w-4 h-4 text-gray-600" />
          <span className="text-sm font-medium">Extract web content</span>
        </div>

        <div className="flex space-x-2 mb-4">
          <input
            type="url"
            placeholder="https://qa-app.allei.net/"
            value={websiteUrl}
            onChange={(e) => setWebsiteUrl(e.target.value)}
            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
          <button
            onClick={() => onStartCrawl(selectedProvider)}
            disabled={isRunDisabled()}
            className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            title={
              !websiteUrl.trim()
                ? "Please enter a website URL"
                : (selectedProvider === Provider.Jina && !crawlOptions.jinaApiKey) || (selectedProvider === 'firecrawl' && !crawlOptions.firecrawlApiKey)
                  ? "Please configure API key for selected provider"
                  : "Start crawling"
            }
          >
            {isCrawling ? <>
              <Loader2 className="w-4 h-4 animate-spin" />
            </> : 'Run'}
          </button>
        </div>

        {/* Options */}
        <div className="border border-gray-200 rounded-lg">
          <button
            onClick={() => setIsOptionsExpanded(!isOptionsExpanded)}
            className="w-full flex items-center justify-between p-3 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            <span>Options</span>
            {isOptionsExpanded ? (
              <ChevronUp className="w-4 h-4" />
            ) : (
              <ChevronDown className="w-4 h-4" />
            )}
          </button>

          {isOptionsExpanded && (
            <div className="border-t border-gray-200 p-4 space-y-4">
              <div className="grid grid-cols-2 sm:grid-cols-1 gap-4">
                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={crawlOptions.crawlSubPages}
                    onChange={(e) => setCrawlOptions({
                      ...crawlOptions,
                      crawlSubPages: e.target.checked
                    })}
                    className="rounded border-gray-300"
                  />
                  <span className="text-sm">Crawl sub-pages</span>
                </label>

                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={crawlOptions.useSitemap}
                    onChange={(e) => setCrawlOptions({
                      ...crawlOptions,
                      useSitemap: e.target.checked
                    })}
                    className="rounded border-gray-300"
                  />
                  <span className="text-sm">Use sitemap</span>
                </label>
              </div>

              {/* <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs text-gray-600 mb-1">Includes</label>
                  <input
                    type="text"
                    value={crawlOptions.includes}
                    onChange={(e) => setCrawlOptions({
                      ...crawlOptions,
                      includes: e.target.value
                    })}
                    className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
                    placeholder="URL patterns to include"
                  />
                </div>

                <div>
                  <label className="block text-xs text-gray-600 mb-1">Excludes</label>
                  <input
                    type="text"
                    value={crawlOptions.excludes}
                    onChange={(e) => setCrawlOptions({
                      ...crawlOptions,
                      excludes: e.target.value
                    })}
                    className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
                    placeholder="URL patterns to exclude"
                  />
                </div>
              </div> */}

              <div>
                <label className="block text-xs text-gray-600 mb-1">Page limit</label>
                <input
                  type="number"
                  value={crawlOptions.limit}
                  onChange={(e) => setCrawlOptions({
                    ...crawlOptions,
                    limit: parseInt(e.target.value) || 10
                  })}
                  className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
                  min="1"
                  max="100"
                />
              </div>
            </div>
          )}
        </div>

        {/* Crawl Results */}
        {(isCrawling || crawlResults) && (
          <div className="mt-6 border border-gray-200 rounded-lg p-4">
            {isCrawling ? (
              <div className="text-center py-4">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mx-auto mb-2"></div>
                <p className="text-sm text-gray-600">Crawling website...</p>
              </div>
            ) : crawlResults && (
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h4 className="text-sm font-medium">Crawl Results</h4>
                  <div className="flex items-center space-x-2">
                    <span className="text-xs text-gray-500">
                      {crawlResults?.data?.length} pages found
                    </span>
                    <button
                      onClick={onResetResults}
                      className="text-xs text-blue-600 hover:text-blue-700"
                    >
                      Reset
                    </button>
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={selectedPages.size === crawlResults?.data?.length && crawlResults?.data?.length > 0}
                      onChange={(e) => onSelectAll(e.target.checked)}
                      className="rounded border-gray-300"
                    />
                    <span className="text-sm">Select all pages</span>
                  </label>

                  <div className='!max-h-[400px] overflow-auto scrollbar-hide'>
                    {(Array.isArray(crawlResults?.data)) && crawlResults.data.map((page: any, index) =>
                      page?.title !== "" ? (
                        <div key={index}>
                          <label className="flex items-start space-x-2">
                            <input
                              type="checkbox"
                              checked={selectedPages.has(index)}
                              onChange={(e) => onPageSelection(index, e.target.checked)}
                              className="rounded border-gray-300 mt-1"
                            />
                            <div className="flex-1">
                              <div className="text-sm font-medium text-gray-900">{page?.title.split('.')[0]}</div>
                              <div className="text-xs text-gray-500">{page?.url}</div>
                            </div>
                          </label>
                        </div>
                      ) : null
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default WebsiteCrawlSection;
